import React, { useRef } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import {
  Container,
  Grid,
  Box,
  Typography,
  Paper,
} from "@mui/material";
import {
  Psychology,
  Hub,
  Shield,
  Groups,
} from "@mui/icons-material";

/* ===================== DATA ===================== */
const visionItems = [
  {
    title: "Agentic Intelligence",
    desc: "Autonomous AI agents capable of planning, execution, and self-improvement.",
    icon: <Hub />,
  },
  {
    title: "Cognitive Reasoning",
    desc: "Abstract reasoning, causality, and goal-oriented intelligence.",
    icon: <Psychology />,
  },
  {
    title: "Ethical Governance",
    desc: "Explainable, compliant AI built for institutional trust.",
    icon: <Shield />,
  },
  {
    title: "Human–AI Partnership",
    desc: "AI designed to amplify human judgment and creativity.",
    icon: <Groups />,
  },
];

/* ===================== METAVERSE SHAPES ===================== */
const MetaverseShapes = ({ rx, ry }) => {
  const shapes = [
    { size: 180, top: "8%", left: "6%", delay: 0 },
    { size: 140, top: "62%", left: "4%", delay: 2 },
    { size: 220, top: "18%", right: "6%", delay: 1 },
    { size: 160, top: "66%", right: "10%", delay: 3 },
  ];

  return (
    <>
      {shapes.map((s, i) => (
        <motion.div
          key={i}
          style={{
            position: "absolute",
            top: s.top,
            left: s.left,
            right: s.right,
            width: s.size,
            height: s.size,
            rotateX: rx,
            rotateY: ry,
            pointerEvents: "none",
            zIndex: 0,
          }}
          animate={{ rotateZ: 360 }}
          transition={{
            repeat: Infinity,
            duration: 40 + i * 10,
            ease: "linear",
            delay: s.delay,
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              border: "1px solid rgba(255,77,77,0.25)",
              boxShadow: "0 0 35px rgba(255,77,77,0.25)",
              position: "relative",
              opacity: 0.55,
              "&::after": {
                content: '""',
                position: "absolute",
                inset: "18%",
                borderRadius: "50%",
                border: "1px dashed rgba(255,255,255,0.25)",
              },
            }}
          />
        </motion.div>
      ))}
    </>
  );
};

/* ===================== CARD ANIMATION ===================== */
const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.12,
      duration: 0.6,
      ease: "easeOut",
    },
  }),
};

/* ===================== COMPONENT ===================== */
export default function NextGenAIVision() {
  const ref = useRef(null);

  /* --- Mouse-based 3D parallax --- */
  const mx = useMotionValue(0);
  const my = useMotionValue(0);

  const rx = useTransform(my, [-120, 120], [6, -6]);
  const ry = useTransform(mx, [-120, 120], [-6, 6]);

  const handleMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set(e.clientX - r.left - r.width / 2);
    my.set(e.clientY - r.top - r.height / 2);
  };

  return (
    <Box
      ref={ref}
      onMouseMove={handleMove}
      sx={{
        position: "relative",
        overflow: "hidden",
        background:
          "radial-gradient(circle at top, rgba(255,77,77,0.15), transparent 40%), linear-gradient(180deg, #050814, #03040b)",
      }}
    >
      {/* ===== METAVERSE SHAPES ===== */}
      <MetaverseShapes rx={rx} ry={ry} />

      <Container sx={{ py: { xs: 10, md: 14 }, position: "relative", zIndex: 2 }}>
        {/* ===== HEADER ===== */}
        <Box textAlign="center" mb={9}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              letterSpacing: "-0.03em",
              background: "linear-gradient(90deg, #fff, #ff4d4d)",
              WebkitBackgroundClip: "text",
              color: "transparent",
              mb: 2.5,
            }}
          >
            Next-Generation AI Vision
          </Typography>

          <Typography
            sx={{
              maxWidth: 780,
              mx: "auto",
              color: "rgba(255,255,255,0.75)",
              lineHeight: 1.85,
            }}
          >
            Autonomous, governed intelligence systems designed for enterprise
            resilience, ethical alignment, and long-term human impact.
          </Typography>
        </Box>

        {/* ===== PRINCIPLES GRID ===== */}
        <Grid container spacing={4}>
          {visionItems.map((item, i) => (
            <Grid item xs={12} md={3} key={i}>
              <motion.div
                custom={i}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                whileHover={{ y: -12 }}
                style={{
                  height: "100%",
                  rotateX: rx,
                  rotateY: ry,
                }}
              >
                <Paper
                  sx={{
                    height: 320,
                    p: 3.5,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    textAlign: "center",
                    background:
                      "linear-gradient(135deg, rgba(24,28,55,0.95), rgba(10,14,30,0.95))",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 4,
                    boxShadow: "0 20px 50px rgba(0,0,0,0.45)",
                    transition: "0.35s ease",
                    "&:hover": {
                      borderColor: "#ff4d4d",
                      boxShadow: "0 35px 90px rgba(139,0,0,0.55)",
                    },
                  }}
                >
                  {/* ICON */}
                  <motion.div
                    whileHover={{ scale: 1.12, rotate: 8 }}
                    transition={{ type: "spring", stiffness: 260, damping: 14 }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        mx: "auto",
                        mb: 2.5,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background:
                          "linear-gradient(45deg, #8b0000, #ff4d4d)",
                        boxShadow: "0 0 20px rgba(255,77,77,0.6)",
                        color: "#fff",
                      }}
                    >
                      {item.icon}
                    </Box>
                  </motion.div>

                  {/* TEXT */}
                  <Box>
                    <Typography fontWeight={800} color="white" gutterBottom>
                      {item.title}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: "rgba(255,255,255,0.7)",
                        lineHeight: 1.7,
                      }}
                    >
                      {item.desc}
                    </Typography>
                  </Box>

                  {/* GLOW LINE */}
                  <Box
                    sx={{
                      height: 3,
                      width: "60%",
                      mx: "auto",
                      mt: 3,
                      borderRadius: 999,
                      background:
                        "linear-gradient(90deg, transparent, #ff4d4d, transparent)",
                      opacity: 0.6,
                    }}
                  />
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
